import { useAuth0 } from '@auth0/auth0-react';
import React, { ReactNode, useContext } from 'react';
import { ReactReduxContext } from 'react-redux';

import { GlobalReducerState } from 'app/reducers';
import {
  makeGetJvpHasPublicationWithFlag,
  makeGetSSOConnectionName,
  makeSelectJvpField,
} from 'Containers/App/selectors';
import {
  BcHandoffLogin,
  getFullAlexUrl,
  markBcHandoffResume,
  stripSourceComponent,
} from 'Containers/App/utils';
import { makeGetProfileIsEmpty } from 'Containers/ProfilePage/selectors';
import { useProfileContext } from 'Contexts/profileContext';
import { useTextContext } from 'Contexts/textContext';
import { Button, ButtonOptions } from 'DesignLibrary/atoms';
import { sendInsightsEvent } from 'Utils/insights';
import { WELCOME_PATH } from 'Utils/urls';

export type BaseButtonProps = {
  children: ReactNode;
  returnToUrl?: string;
  getStore?: () => GlobalReducerState;
} & ButtonOptions;

export interface AuthButtonReduxProps {
  userId: string | undefined;
  ssoEnabled: boolean;
  ssoConnectionName: string;
  isProfileEmpty: boolean;
}

export interface AuthButtonProps {
  action: 'login' | 'signUp';
}

interface AlexIdLoginOptions extends AuthButtonReduxProps, AuthButtonProps {
  returnToUrl: string;
  getStore?: () => GlobalReducerState;
}

// Sends the user through Auth0's login, saving what Go needs to restore their progress on return.
export const useAlexIdLogin = () => {
  const { loginWithRedirect } = useAuth0();
  const { textMap } = useTextContext();
  const { profileProgressMap } = useProfileContext();
  const { locale } = useTextContext();

  return ({
    action,
    getStore,
    userId,
    ssoEnabled,
    ssoConnectionName,
    isProfileEmpty,
    returnToUrl,
  }: AlexIdLoginOptions) => {
    const unvalidatedSegmentSlug = getStore?.().globalApp.unvalidatedSegmentSlug;
    const alexUrl = getFullAlexUrl(window.location, unvalidatedSegmentSlug);
    const authLocale = locale.slice(0, 2);
    // For SSO: use connection parameter to bypass Auth0 login screen
    // For non-SSO: use screen_hint to control login vs signup screen (New Universal Login)
    // Note: 'signup' must be lowercase for Auth0 New Universal Login
    // Previously used login_hint which worked with Classic Universal Login but not New Universal Login
    const ssoOptions = ssoEnabled
      ? { connection: ssoConnectionName }
      : { screen_hint: action === 'signUp' ? 'signup' : undefined };

    const storeOnRedirect = {
      ...getStore?.(),
    };

    window.onbeforeunload = null;

    if (window.location.pathname !== WELCOME_PATH) {
      localStorage.setItem('store', JSON.stringify(storeOnRedirect));
      localStorage.setItem('contentful', JSON.stringify(textMap));
      localStorage.setItem('profileMap', JSON.stringify(profileProgressMap));
    }

    const goGuestId = stripSourceComponent(userId || '');

    const redirectOptions = {
      appState: {
        returnTo: returnToUrl,
      },
      authorizationParams: {
        alex_url: alexUrl,
        jv_guest_id: goGuestId && !isProfileEmpty ? goGuestId : undefined,
        ...ssoOptions,
        ui_locales: authLocale,
      },
    };

    sendInsightsEvent(null, `auth_${action.toLowerCase()}`);

    loginWithRedirect({
      ...redirectOptions,
    });
  };
};

const AuthButton = ({
  children,
  action,
  getStore,
  userId,
  ssoEnabled,
  ssoConnectionName,
  isProfileEmpty,
  returnToUrl = window.location.pathname,
  ...rest
}: AuthButtonProps & BaseButtonProps & AuthButtonReduxProps) => {
  const startLogin = useAlexIdLogin();
  const handleLogin = () =>
    startLogin({ action, getStore, userId, ssoEnabled, ssoConnectionName, isProfileEmpty, returnToUrl });

  return (
    <Button onClick={() => handleLogin()} {...rest}>
      {children}
    </Button>
  );
};

// What the trip from Go to BC uses when Go's login has gone stale: a silent renewal, then a real
// login when Auth0 needs one. With resumeAfterLogin, the trip to BC continues once the user is back.
export const useBcHandoffLogin = ({ resumeAfterLogin }: { resumeAfterLogin: boolean }): BcHandoffLogin => {
  const { getAccessTokenSilently } = useAuth0();
  const startLogin = useAlexIdLogin();
  // Read when the user is sent to log in, not on render, so callers need no store of their own.
  const reduxContext = useContext(ReactReduxContext);

  return {
    renewLogin: () => getAccessTokenSilently({ cacheMode: 'off', timeoutInSeconds: 10 }),
    loginAgain: () => {
      const getStore = reduxContext ? () => reduxContext.store.getState() as GlobalReducerState : undefined;
      const state = getStore?.();
      if (resumeAfterLogin) markBcHandoffResume();
      startLogin({
        action: 'login',
        getStore,
        userId: state && makeSelectJvpField('userId')(state),
        ssoEnabled: Boolean(state && makeGetJvpHasPublicationWithFlag('sso_enabled')(state)),
        ssoConnectionName: state ? makeGetSSOConnectionName()(state) : '',
        isProfileEmpty: Boolean(state && makeGetProfileIsEmpty()(state)),
        returnToUrl: window.location.pathname,
      });
    },
  };
};

export default AuthButton;
